import styled from "styled-components";
import { motion } from "framer-motion";
import { FC, FormEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Modal } from "@/components/Modal";
import { CloseButton } from "@/components/AlertDialogs/Dialog";
import Text from "@/components/Text";
import FormField from "@/components/Forms/FormField";
import Button from "@/components/Button";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/utils/api";
import { Option } from "@/components/Forms/Dropdown";

const Dialog = styled(motion.div)`
  width: 384px;
  max-width: 90%;
  padding-top: 56px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  position: relative;
`;

const Content = styled.form`
  background: ${({ theme }) => theme.colors.white};
  padding: 0 24px 40px;
`;

const Heading = styled(Text)`
  margin-bottom: 4px;
`;

const HeadingsHolder = styled.div`
  margin-bottom: 24px;
  text-align: center;
`;

const ButtonsHolder = styled.div`
  margin-top: 32px;
  display: flex;
  gap: 16px;
  > button {
    flex: 1;
  }
`;

interface Props {
  onClose: () => void;
  bonusId: number;
}

type FormDataT = {
  optimisation_test_unit_id: number;
  status: "approved" | "rejected";
};

const GamificationWinnerModal: FC<Props> = ({ onClose, bonusId }) => {
  const [options, setOptions] = useState<Option<number>[]>([
    { label: "No Winner", value: 0 },
  ]);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    setError,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      status: "rejected",
      optimisation_test_unit_id: 0,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ status, optimisation_test_unit_id }) => {
      try {
        await api.patch(`/v1/gamification/thumbnails/bonuses/${bonusId}`, {
          status,
          ...(!!optimisation_test_unit_id && {
            optimisation_test_unit_id,
          }),
        });

        await queryClient.invalidateQueries(
          ["bonuses", { type: "thumbnails", status: "pending" }],
          {
            exact: false,
          }
        );

        alerts.success("Competition winner was successfully changed");

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  useEffect(() => {
    api
      .get<never, Array<{ userName: string; ctr: string; id: number }>>(
        `/v1/gamification/thumbnails/bonuses/${bonusId}/competitors`
      )
      .then((participants) => {
        setOptions([
          { label: "No Winner", value: 0 },
          ...participants.map((p) => ({
            label: `${p.userName} · CTR ${p.ctr} %`,
            value: p.id,
          })),
        ]);
      });
  }, [bonusId]);

  return (
    <Modal>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Content onSubmit={onSubmit}>
          <HeadingsHolder>
            <Heading $type="heading2">Other winner</Heading>
            <Text>Select other competitor as a winner of this project</Text>
          </HeadingsHolder>
          <FormField<FormDataT, number>
            name="optimisation_test_unit_id"
            type="select"
            options={options}
            control={control}
          />
          <ButtonsHolder>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Save
            </Button>
            <Button $type="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </ButtonsHolder>
        </Content>
      </Dialog>
    </Modal>
  );
};

export default GamificationWinnerModal;
